import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { CircularProgress, Skeleton, useMediaQuery } from "@mui/material";
import Image from "next/image";
import { useParams, useRouter } from "next/navigation";
import { useSession } from "next-auth/react";
import { Pagination, Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import AvailableHours from "./availableHours";
import ModalCancelaciones from "./ModalCancelaciones";
import ModalFareharborExtraOptions from "./modalFareharborExtraOptions";
import ModalTyc from "./ModalTyc";
import SelectTariff from "./selectTariff";
import GoogleTranslationComponent from "../googleTranslate/GoogleTranslationComponent";
import { useTranslation } from "@/app/i18n/client";
import starIcon from "@/assets/images/icons/starIcon.svg";
import iconPago from "@/assets/images/icons/vee-pago-seguro.png";
import iconoReembolso from "@/assets/images/icons/vee-reembolso.png";
import veeIcon from "@/assets/images/icons/veeIcon.svg";
import CustomButton from "@/common/customElements/button";
import StaticDatePicker from "@/common/customElements/staticDatePicker";
import { CustomDiv, HorizontalCenterDiv, InlineDiv } from "@/common/divs";
import {
  AlmostBoldText,
  LightText,
  LightTextSpan,
  RegularText,
  SemiBoldText,
} from "@/common/texts";
import googleIcon from "@/components/city-landing/images/google_icon.svg";
import languageIcon from "@/components/city-landing/images/languageIcon.svg";
import tripAdvisorIcon from "@/components/city-landing/images/tripadvisor_logo.svg";
import BookingContext from "@/context/bookingContext/bookingContext";
import { getToken, initTokenWithEmail } from "@/context/NonAuthProvider";
import { useActivityDate } from "@/hooks/useActivityDate";
import {
  ActivityAddressVm,
  ActivityDetailGeoSearch,
  CreateGroupReservationRequest,
  CreateIndividualDetailedReservationRequest,
  CreateIndividualReservationRequest,
  CreateReservationRequestTariffType,
} from "@/model";
import { BookingContextType, PriceDetailType } from "@/types/components";
import {
  FAKE_ID_FOR_EXTRA_AUDIENCE,
  NEUTRO_TITLES_COLOR,
  PRIMARY_BUTTON_COLOR,
  PRIMARY_BUTTON_COLOR_DISSABLED,
} from "@/utils/constants";
import { getCoverImage, getStringDateToShow } from "@/utils/functions";
import { getSubcategoryIconProdEnv } from "@/utils/subcategories";
// Import Swiper styles
import "swiper/css";
import "@/components/home/videoCarousel/styles.css";
import "swiper/css/effect-coverflow";
import "swiper/css/pagination";
import "swiper/css/navigation";
import "@/components/city-landing/landing.css";

const ReservationComponent = ({
  activityInfo,
  location,

  calendarRef,
  activityId,
}: {
  activityInfo: ActivityDetailGeoSearch;
  location: ActivityAddressVm | undefined;

  calendarRef: React.RefObject<HTMLDivElement>;
  activityId?: string;
}) => {
  const { id: paramsId } = useParams<{ id: string }>();

  const isMobile = useMediaQuery("(max-width:600px)");
  const isSmallMobile = useMediaQuery("(max-width:450px)");

  const id = useMemo(() => {
    if (activityId) {
      return activityId;
    }

    return paramsId;
  }, [paramsId, activityId]);

  const {
    availableDatesState,
    dateRange,
    hoursAvailable,
    isFetchingHours,
    selectedDate,
    handleSelectDate,
    handleChangeMonth,
    minDate,
    selectedHour,
    setSelectedHour,
    priceRef,
    detail_info,
    startDate,
    terms_and_conditions,
    showCalendar,
    loadingHours,
    setIsDaySelected,
    recomendations,
    loadingRecommendations,
  } = useActivityDate(id, activityInfo);
  const [price, setPrice] = useState("0");
  const { t, i18n } = useTranslation();
  const [openModalTyc, setOpenModalTyc] = useState(false);
  const [openModalCancelaciones, setOpenModalCancelaciones] = useState(false);
  const [disableBookButton, setDisableBookButton] = useState(true);
  const { saveBookingInfo } = useContext(BookingContext) as BookingContextType;
  const price_detail = useRef<PriceDetailType[]>(null);
  const tariffId = useRef<number>(0);
  const tariffType = useRef<CreateReservationRequestTariffType>("INDIVIDUAL");
  const router = useRouter();
  const session = useSession();
  const [openAdditionalOptionsModal, setOpenAdditionalOptionsModal] =
    useState(false);
  useEffect(() => {
    setPrice("0");
  }, [selectedDate]);

  const handleSaveBookingInfo = async () => {
    const audienceSeats = [];
    let extraIndividual = 0;
    if (price_detail.current) {
      for (let i = 0; i < price_detail.current.length; i++) {
        if (price_detail.current[i].audienceId != FAKE_ID_FOR_EXTRA_AUDIENCE) {
          audienceSeats.push({
            type: price_detail.current[i].audienceType,
            audienceId: price_detail.current[i].audienceId,
            seats: parseInt(price_detail.current[i].quantity),
            tariffId: price_detail.current[i].tariffId,
          });
        } else {
          extraIndividual = parseInt(price_detail.current[i].quantity);
        }
      }
    }
    const commonPayload = {
      scheduleId: selectedHour?.schedule?.id ?? 0,
      date: selectedDate.date,
      travelerEmailContact: "",
      audienceSeats: audienceSeats,
    };
    const booking_info = {
      activity_name: activityInfo.name,
      place: location?.formattedAddress ?? "",
      date_and_hour: `${getStringDateToShow(selectedDate.date)} - ${selectedHour?.start} hrs `,
      price_detail: price_detail.current,
      total_price: parseFloat(priceRef.current),
      id: id,
      expire_date: new Date(selectedDate.date),
      activity_image:
        activityInfo.images ? getCoverImage(activityInfo.images) : "",
      tariffType: tariffType.current,
      booking_payload: {} as
        | CreateIndividualReservationRequest
        | CreateIndividualDetailedReservationRequest
        | CreateGroupReservationRequest,
    };

    if (tariffType.current === "INDIVIDUAL") {
      booking_info.booking_payload = {
        ...commonPayload,
        tariffId: tariffId.current,
      };
    } else if (tariffType.current === "GROUP") {
      booking_info.booking_payload = {
        ...commonPayload,
        tariffId: tariffId.current,
        groups:
          price_detail.current?.[0]?.quantity ?
            parseInt(price_detail.current?.[0]?.quantity)
          : 0,
        extraIndividual: extraIndividual,
      };
    } else if (tariffType.current === "INDIVIDUAL_DETAILED") {
      booking_info.booking_payload = {
        ...commonPayload,
        tariffId: tariffId.current,
      };
    }
    saveBookingInfo(booking_info);

    if (session.data == null && getToken() == "") {
      await initTokenWithEmail();
    }

    //@ts-ignore
    if (activityInfo.fareharborActivity == true) {
      //Abrimos el modal mostrando una rueda de carga
      //Si hay opciones adicionales las mostramos antes de hacer la reserva
      //Redirigimos a la pagina de reserva
      setOpenAdditionalOptionsModal(true);
    } else {
      router.push("/activity/" + id + "/booking");
    }
  };

  const maxDate = new Date(dateRange?.endDate ?? new Date());
  const [showVisibility, setShowVisibility] = useState(false);
  const getSlug = (activity: ActivityDetailGeoSearch) => {
    if (activity.slugEs) {
      return activity.slugEs;
    } else {
      return id;
    }
  };

  return (
    <div className="calendar-section-container" ref={calendarRef}>
      <div>
        {showCalendar ?
          availableDatesState.length > 0 ?
            <StaticDatePicker
              changeValue={(date: string) => {
                setIsDaySelected(true);
                handleSelectDate(new Date(date));
              }}
              availableDates={availableDatesState}
              changeMonth={handleChangeMonth}
              dateFormat="dd/mm/yyyy"
              height="608px"
              minDate={minDate}
              showVisibility={showVisibility}
              setShowVisibility={setShowVisibility}
              maxDate={maxDate}
              startDate={startDate}
            />
          : <HorizontalCenterDiv
              className="calendar-reservation-title"
              style={{
                width: isMobile ? "340px" : "482px",
                marginTop: "40px",
                borderRadius: "16px",
                display: showVisibility ? "none" : "block",
                paddingTop: "14px",
              }}
            >
              <AlmostBoldText fontSize="18px">
                <GoogleTranslationComponent text="No hay fechas disponibles" />
              </AlmostBoldText>
            </HorizontalCenterDiv>

        : <>
            <Skeleton
              className="auto-mobile-width"
              style={{
                marginTop: isMobile ? "-160px" : "0px",
                height: "608px",
                width: isMobile ? "340px" : "482px",
                borderRadius: "16px",
              }}
            ></Skeleton>
            <HorizontalCenterDiv
              width={isMobile ? "340px" : "482px"}
              style={{ position: "absolute", top: "0px" }}
            >
              <AlmostBoldText>
                <GoogleTranslationComponent text="Buscando fechas disponibles" />
              </AlmostBoldText>
            </HorizontalCenterDiv>
          </>
        }
        {selectedDate.isSelected ?
          loadingHours ?
            <InlineDiv
              width="482px"
              paddingY="16px"
              paddingX="25px"
              className="available-hours-container"
            >
              <CircularProgress></CircularProgress>
            </InlineDiv>
          : hoursAvailable ?
            <AvailableHours
              hoursAvailable={hoursAvailable}
              setSelectedHour={setSelectedHour}
              selectedHour={selectedHour}
            />
          : !isFetchingHours ?
            <InlineDiv
              width="482px"
              paddingY="16px"
              paddingX="25px"
              className="available-hours-container"
            >
              <SemiBoldText fontSize="18px" style={{ width: "100%" }}>
                {t("reservation_schedule.no_schedules_available")}
              </SemiBoldText>
            </InlineDiv>
          : null
        : null}
        {selectedHour && (
          <SelectTariff
            selectedHour={selectedHour}
            priceRef={priceRef}
            setDisableBookButton={setDisableBookButton}
            price_detail={price_detail}
            tariffId={tariffId}
            tariffType={tariffType}
            price={price}
            setPrice={setPrice}
          />
        )}
        {showVisibility && (
          <HorizontalCenterDiv
            width="482px"
            paddingY="16px"
            className="reservation-container"
            style={{ flexDirection: "column" }}
          >
            <CustomButton
              width={
                window.screen.availWidth < 430 ? "302px"
                : window.screen.availWidth < 525 ?
                  "382px"
                : "432px"
              }
              height="60px"
              backgroundColor={
                activityInfo.fareharborActivity ?
                  disableBookButton ?
                    PRIMARY_BUTTON_COLOR_DISSABLED
                  : PRIMARY_BUTTON_COLOR
                : parseFloat(price) == 0 ?
                  PRIMARY_BUTTON_COLOR_DISSABLED
                : disableBookButton ?
                  PRIMARY_BUTTON_COLOR_DISSABLED
                : PRIMARY_BUTTON_COLOR
              }
              onClick={() => {
                activityInfo.fareharborActivity ?
                  disableBookButton ? null
                  : handleSaveBookingInfo()
                : parseFloat(price) == 0 ? null
                : disableBookButton ? null
                : handleSaveBookingInfo();
              }}
            >
              <RegularText fontSize="18px">
                {" "}
                {t("activity.continue_with_booking")}
              </RegularText>
            </CustomButton>
            <InlineDiv
              style={{ marginTop: "10px", alignItems: "center", gap: "5px" }}
            >
              <Image
                width={40}
                height={40}
                src={iconoReembolso}
                alt="reembolso"
              ></Image>
              <AlmostBoldText
                style={{
                  maxWidth: i18n.language == "en" ? "170px" : "150px",
                  lineHeight: "20px",
                  cursor: "pointer",
                }}
                onClick={() => {
                  setOpenModalTyc(true);
                }}
              >
                {t("activity.schedule.reembolso")}
              </AlmostBoldText>
              <Image
                width={40}
                height={40}
                src={iconPago}
                alt="reembolso"
              ></Image>
              <AlmostBoldText> {t("activity.schedule.pago")}</AlmostBoldText>
            </InlineDiv>
          </HorizontalCenterDiv>
        )}
      </div>
      <div
        style={{
          marginTop: "30px",
          justifyContent: "center",
          width: isMobile ? "350px" : "482px",
        }}
      >
        {loadingRecommendations ?
          <>
            <HorizontalCenterDiv
              style={{
                width: isMobile ? "340px" : "482px",
                borderRadius: "16px",
                display: showVisibility ? "none" : "flex",
                flexDirection: "column",
                textAlign: "center",
              }}
            >
              <AlmostBoldText fontSize="18px">
                <GoogleTranslationComponent text="Buscando actividades recomendadas" />
              </AlmostBoldText>
              <CircularProgress className="mt-2"></CircularProgress>
            </HorizontalCenterDiv>
          </>
        : recomendations.length > 0 && (
            <>
              <HorizontalCenterDiv
                style={{
                  width: isMobile ? "340px" : "482px",
                  borderRadius: "16px",
                  display: showVisibility ? "none" : "block",
                  textAlign: "center",
                }}
              >
                <AlmostBoldText fontSize="18px">
                  <GoogleTranslationComponent text="Te mostramos algunas actividades recomendadas" />
                </AlmostBoldText>
              </HorizontalCenterDiv>
              <Swiper
                style={{ paddingBottom: isMobile ? "60px" : "10px" }}
                spaceBetween={isMobile ? 10 : 70}
                slidesPerView={"auto"}
                navigation={isMobile ? false : true}
                grabCursor={false}
                centeredSlides={isMobile ? true : true}
                watchSlidesProgress={true}
                loop={isMobile ? true : true}
                modules={[Navigation, Pagination]}
              >
                {recomendations.map((element, index) => {
                  return (
                    element.id != activityInfo.id && (
                      <SwiperSlide
                        style={{
                          cursor: "pointer",
                          width: isMobile ? "350px" : "400px",
                          backgroundColor: NEUTRO_TITLES_COLOR,
                        }}
                        key={index}
                        onClick={() => {
                          router.push("/activity/" + getSlug(element));
                        }}
                      >
                        <CustomDiv
                          key={element.id}
                          width={isMobile ? "350px" : "400px"}
                          height={isSmallMobile ? "410px" : "470px"}
                          className="div-activity"
                        >
                          <CustomDiv
                            height={isMobile ? "230px" : "230px"}
                            style={{
                              position: "relative",
                            }}
                          >
                            <div
                              className="image-div"
                              style={{
                                backgroundImage:
                                  element.images ?
                                    "linear-gradient(to top, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.0)), url(" +
                                    element.images[0].link +
                                    ")"
                                  : "",
                              }}
                            />

                            <InlineDiv
                              style={{
                                justifyContent: "space-between",
                                maxWidth: isMobile ? "200px" : "300px",
                                marginTop: isMobile ? "5px" : "15px",
                                marginLeft: "-10px",
                              }}
                            >
                              <InlineDiv marginX="10px">
                                {element.fh_tripadvisor_rating ?
                                  element.fh_tripadvisor_rating > 0 ?
                                    <InlineDiv
                                      width="80px"
                                      style={{
                                        justifyContent: "space-evenly",
                                        background: "white",
                                        marginLeft: "10px",
                                        borderRadius: "16px",
                                        alignItems: "center",
                                      }}
                                    >
                                      <Image
                                        style={{ marginTop: "1px" }}
                                        src={tripAdvisorIcon}
                                        alt="tripAdvisor"
                                        width={25}
                                        height={25}
                                      />
                                      <AlmostBoldText
                                        style={{
                                          fontWeight: "400",
                                          color: "black",
                                        }}
                                      >
                                        {element.fh_tripadvisor_rating}
                                      </AlmostBoldText>
                                      <Image
                                        src={starIcon}
                                        alt="star"
                                        width={22}
                                        height={22}
                                        style={{ marginTop: "1px" }}
                                      />
                                    </InlineDiv>
                                  : null
                                : null}
                                {element.fh_google_rating ?
                                  element.fh_google_rating > 0 ?
                                    <InlineDiv
                                      width="80px"
                                      style={{
                                        justifyContent: "space-evenly",
                                        background: "white",
                                        marginLeft: "10px",
                                        borderRadius: "16px",
                                        alignItems: "center",
                                      }}
                                    >
                                      <Image
                                        src={googleIcon}
                                        alt="google"
                                        width={24}
                                        height={24}
                                        style={{
                                          maxHeight: "24px",
                                          maxWidth: "24px",
                                          marginTop: "0.5px",
                                        }}
                                      />
                                      <AlmostBoldText
                                        style={{
                                          fontWeight: "400",
                                          color: "black",
                                          lineHeight: "0px",
                                        }}
                                      >
                                        {element.fh_google_rating}
                                      </AlmostBoldText>
                                      <Image
                                        src={starIcon}
                                        alt="star"
                                        width={22}
                                        height={22}
                                        style={{ marginTop: "1px" }}
                                      />
                                    </InlineDiv>
                                  : null
                                : null}
                                {element.rating ?
                                  element.rating > 0 ?
                                    <InlineDiv
                                      width="80px"
                                      style={{
                                        justifyContent: "space-evenly",
                                        alignItems: "center",
                                      }}
                                    >
                                      <Image
                                        src={veeIcon}
                                        alt="tripAdvisor"
                                        width={29}
                                        height={29}
                                        style={{ marginTop: "1px" }}
                                      />
                                      <AlmostBoldText
                                        style={{
                                          fontWeight: "400",
                                          color: "white",
                                        }}
                                      >
                                        {element.rating}
                                      </AlmostBoldText>
                                      <Image
                                        src={starIcon}
                                        alt="star"
                                        width={22}
                                        height={22}
                                      />
                                    </InlineDiv>
                                  : null
                                : null}
                              </InlineDiv>
                            </InlineDiv>
                          </CustomDiv>
                          <CustomDiv
                            paddingX={isMobile ? "5px" : "16px"}
                            paddingY={isMobile ? "5px" : "16px"}
                            style={{
                              flexGrow: 1,
                              borderBottomLeftRadius: "16px",
                              borderBottomRightRadius: "16px",
                            }}
                            background={NEUTRO_TITLES_COLOR}
                          >
                            <AlmostBoldText
                              className="overflowOnTwoLines"
                              color="#ffffff"
                              fontSize="18px"
                              style={{
                                textAlign: "left",
                                paddingBottom: "0px",
                                minHeight: "54px",
                              }}
                            >
                              <GoogleTranslationComponent text={element.name} />
                            </AlmostBoldText>
                            <InlineDiv
                              style={{
                                gap: "20px",
                                marginTop: "10px",
                                marginBottom: "10px",
                              }}
                            >
                              <InlineDiv style={{ gap: "5px" }}>
                                <img
                                  style={{ width: "25px", height: "25px" }}
                                  alt="subcaegory_icon"
                                  width={25}
                                  src={getSubcategoryIconProdEnv(
                                    element.category.id,
                                    element.subcategory?.id ?? 0,
                                  )}
                                />
                                <LightText
                                  fontSize="14px"
                                  color="#ffffff"
                                  style={{ textAlign: "left" }}
                                >
                                  <GoogleTranslationComponent
                                    text={element.subcategory?.name ?? ""}
                                  />
                                </LightText>
                              </InlineDiv>
                              <InlineDiv style={{ gap: "5px" }}>
                                <img
                                  style={{ width: "27px", height: "27px" }}
                                  alt="subcaegory_icon"
                                  width={25}
                                  src={languageIcon.src}
                                />
                                <LightText
                                  fontSize="14px"
                                  className="language-text"
                                  color="#ffffff"
                                >
                                  {element.languages.map((language, i) => {
                                    if (i == element.languages.length - 1) {
                                      return language.name;
                                    }
                                    return language.name + ", ";
                                  })}
                                </LightText>
                              </InlineDiv>
                            </InlineDiv>
                            <SemiBoldText
                              fontSize="14px"
                              className={
                                isMobile ? "overflowOnTwoLines" : (
                                  "overflowOnThreeLines"
                                )
                              }
                              style={{
                                maxHeight: "100px",
                                minHeight: "63px",
                                color: "white",
                              }}
                            >
                              <GoogleTranslationComponent
                                text={element.description}
                              />
                            </SemiBoldText>
                            <InlineDiv
                              style={{
                                marginTop: "10px",
                                justifyContent: "space-between",
                              }}
                            >
                              <LightTextSpan
                                style={{ fontWeight: "500" }}
                                fontSize="20px"
                                color="#ffffff"
                              >
                                {t("activity.from")}{" "}
                                <LightTextSpan
                                  style={{ fontWeight: "500" }}
                                  fontSize="20px"
                                  color="#ffcc00"
                                >
                                  {element.priceWithTaxFormatted}{" "}
                                  {t("home.price_per_person")}
                                </LightTextSpan>
                              </LightTextSpan>
                            </InlineDiv>
                          </CustomDiv>
                        </CustomDiv>
                      </SwiperSlide>
                    )
                  );
                })}
              </Swiper>
            </>
          )
        }
      </div>
      <div className="terms-and-conditions-container">
        {detail_info && (
          <>
            <CustomDiv
              width="482px"
              paddingY="16px"
              paddingX="30px"
              className="terms-and-conditions-card"
            >
              <SemiBoldText fontSize="18px">
                {t("activity.details.includes")}
              </SemiBoldText>

              {detail_info.include ?
                detail_info.include.map(
                  (option, index) =>
                    index < 3 && (
                      <LightText key={index} style={{ marginTop: "8px" }}>
                        •{" "}
                        <GoogleTranslationComponent
                          text={option.description?.substring(0, 200) ?? ""}
                        />
                      </LightText>
                    ),
                )
              : <LightText style={{ marginTop: "8px" }}>
                  • {i18n.t("activity.no_details")}
                </LightText>
              }

              <HorizontalCenterDiv
                onClick={() => {
                  setOpenModalCancelaciones(true);
                }}
              >
                <SemiBoldText
                  fontSize="18px"
                  style={{ marginTop: "8px", cursor: "pointer" }}
                >
                  {t("activities.filter.view_more")}
                </SemiBoldText>
              </HorizontalCenterDiv>
            </CustomDiv>
          </>
        )}
        {terms_and_conditions && (
          <>
            <CustomDiv
              width="482px"
              paddingY="16px"
              paddingX="30px"
              className="terms-and-conditions-card"
            >
              <SemiBoldText fontSize="18px">
                {t("activity.legals.terms")}
              </SemiBoldText>
              {(
                terms_and_conditions.termsAndConditionsPolicy?.description &&
                terms_and_conditions.termsAndConditionsPolicy?.description[0] ==
                  "["
              ) ?
                JSON.parse(
                  terms_and_conditions.termsAndConditionsPolicy?.description,
                ).map(
                  (item: any, index: number) =>
                    index < 3 && (
                      <LightText key={index} style={{ marginTop: "8px" }}>
                        •{" "}
                        <GoogleTranslationComponent
                          text={item.children[0].text.substring(0, 250) ?? ""}
                        />
                      </LightText>
                    ),
                )
              : <LightText style={{ marginTop: "8px" }}>
                  •{" "}
                  {terms_and_conditions.termsAndConditionsPolicy?.description?.substring(
                    0,
                    250,
                  )}
                </LightText>
              }
              <HorizontalCenterDiv
                onClick={() => {
                  setOpenModalTyc(true);
                }}
              >
                <SemiBoldText
                  fontSize="18px"
                  style={{ marginTop: "8px", cursor: "pointer" }}
                >
                  {t("activities.filter.view_more")}
                </SemiBoldText>
              </HorizontalCenterDiv>
            </CustomDiv>
          </>
        )}
      </div>
      <InlineDiv height="40px" />
      {openAdditionalOptionsModal && (
        <ModalFareharborExtraOptions
          activityId={activityInfo.id}
          open={openAdditionalOptionsModal}
          setOpen={setOpenAdditionalOptionsModal}
          scheduleId={selectedHour?.schedule?.id ?? 0}
          tariffsArray={price_detail.current ?? []}
        />
      )}
      {openModalTyc && (
        <ModalTyc
          open={openModalTyc}
          setOpen={setOpenModalTyc}
          terms_and_conditions={terms_and_conditions}
        ></ModalTyc>
      )}
      {openModalCancelaciones && (
        <ModalCancelaciones
          open={openModalCancelaciones}
          setOpen={setOpenModalCancelaciones}
          detail_info={detail_info}
        ></ModalCancelaciones>
      )}
    </div>
  );
};
export default ReservationComponent;
